/**
 * SDK 领红包弹框(券列表 + 一键领取)
 *
 * 点 CouponBanner 入口弹出(不跳新页)。流程:列券(只读,不校验登录)-> 点「一键领取」
 * -> isLoggedIn ? doClaim() : openLogin(() => doClaim())(登录后续接自动领)-> 结果。
 * doClaim = runClaimAll(allSettled 领所有未领券,共享 @/lib/coupon-claim)。
 * portal 到 body 级独立 shadow(复用 modal-portal,盖住客服挂件),复用 .hh-modal__*。
 * 券卡片票券式渲染(本地格式化,不引 date-fns/@/libutils,避免进 SDK bundle)。
 *
 * 布局:卡片固定高度 flex 列--标题/Banner 固定顶部、券列表区滚动(flex:1)、一键领取按钮固定底部,
 * 红包多时仅列表区滚动,按钮与 Banner 不随之移动。数据源 getGroupDetail 一次拿全
 * 头图(head_image_pc_url/head_image_mobile_url)+ coupons 券列表(+ 登录后领取态)。
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getSdkModalPortal } from '../lib/modal-portal';
import { useIsLoggedIn, openLogin } from '../lib/login-manager';
import { getGroupDetail, claimCoupon } from '../api/coupons';
import { readUserStorage } from '@/lib/shared-storage';
import { runClaimAll, type ClaimResult } from '@/lib/coupon-claim';
import type { GroupCoupon, CouponType } from '@/types/coupon';

/** 退出动画时长(对齐 H5 抽屉退场,延迟卸载) */
const CLOSE_ANIM_MS = 220;

/** 分转元(去尾多余零:5000->50 / 5050->50.5 / 5005->50.05) */
function toYuan(fen: number): string {
  return (fen / 100).toFixed(2).replace(/\.?0+$/, '');
}

/** 按 type 渲染左段大号优惠值(对齐主流程 ClaimClient,但本地实现不引 utils) */
function formatCouponValue(type: CouponType, value: number): { big: string; tag?: string } {
  switch (type) {
    case 'discount':
      return { big: `¥${toYuan(value)}` };
    case 'percentage': {
      const v = value / 10;
      const text = Number.isInteger(v) ? `${v}` : v.toFixed(1);
      return { big: `${text}折` };
    }
    case 'fixed_amount':
      return { big: `¥${toYuan(value)}`, tag: '固定价' };
    case 'gift':
      return { big: '赠品' };
    default:
      return { big: '-' };
  }
}

function thresholdText(minSpend: number): string {
  return minSpend > 0 ? `满${toYuan(minSpend)}可用` : '无门槛';
}

/** 本地日期格式化(不引 date-fns,SDK bundle 安全) */
function formatDate(d?: string): string {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}

function validityText(c: GroupCoupon): string {
  const start = formatDate(c.start_at);
  const end = formatDate(c.end_at);
  if (start && end) return `${start} - ${end}`;
  if (end) return `有效期至 ${end}`;
  return '';
}

function claimedBadgeText(c: GroupCoupon): string | null {
  if (c.claimed !== true) return null;
  if (c.issue_status === 'used') return '已使用';
  if (c.issue_status === 'expired') return '已过期';
  return '已领取';
}

/** 票券式卡片:左段品牌色优惠值 + 虚线齿孔 + 右段信息 + 已领取标 */
function CouponCard({ coupon }: { coupon: GroupCoupon }) {
  const badge = claimedBadgeText(coupon);
  const claimed = badge !== null;
  const { big, tag } = formatCouponValue(coupon.type, coupon.value);
  const threshold = thresholdText(coupon.min_spend);
  const validity = validityText(coupon);
  return (
    <div className={claimed ? 'hh-coupon-card hh-coupon-card--claimed' : 'hh-coupon-card'}>
      <div className="hh-coupon-card__value">
        <span className="hh-coupon-card__big">{big}</span>
        <span className="hh-coupon-card__threshold">{threshold}</span>
        {tag && <span className="hh-coupon-card__tag">{tag}</span>}
      </div>
      <div className="hh-coupon-card__body">
        <div className="hh-coupon-card__info">
          <p className="hh-coupon-card__name">{coupon.name}</p>
          {validity && <p className="hh-coupon-card__validity">{validity}</p>}
        </div>
        {badge && <span className="hh-coupon-card__badge">{badge}</span>}
      </div>
    </div>
  );
}

type LoadStatus = 'loading' | 'ready' | 'error';
type ClaimStatus = 'idle' | 'claiming' | 'done';

interface Props {
  /** 券组 code */
  code: string;
  onClose: () => void;
}

export function SdkCouponClaimModal({ code, onClose }: Props) {
  const isLoggedIn = useIsLoggedIn();
  const [portalMount] = useState(getSdkModalPortal);
  const [mounted, setMounted] = useState(true);
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [coupons, setCoupons] = useState<GroupCoupon[]>([]);
  const [headImagePc, setHeadImagePc] = useState<string | undefined>(undefined);
  const [headImageMobile, setHeadImageMobile] = useState<string | undefined>(undefined);
  const [claimStatus, setClaimStatus] = useState<ClaimStatus>('idle');
  const [result, setResult] = useState<ClaimResult | null>(null);

  // 登录用户手机号:作为 member_phone 传入券组查询,上游据此返回领取状态;
  // 未登录不传(领取状态字段为 null,全部视为可领)
  const phone = isLoggedIn ? readUserStorage()?.user?.mobile : undefined;

  // code/phone 变化(如登录续接后 phone 变)时重置 loading(渲染期守卫,避开 set-state-in-effect)
  const requestKey = `${code}:${phone ?? ''}`;
  const [lastRequestKey, setLastRequestKey] = useState(requestKey);
  if (lastRequestKey !== requestKey) {
    setLastRequestKey(requestKey);
    setStatus('loading');
    setCoupons([]);
    setHeadImagePc(undefined);
    setHeadImageMobile(undefined);
  }

  // 拉券包详情(公开端点;登录后带 phone 拿领取态;登录续接后 isLoggedIn 翻 true 重跑刷新)
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const detail = await getGroupDetail(code, phone);
        if (!cancelled) {
          setCoupons(detail.coupons ?? []);
          setHeadImagePc(detail.head_image_pc_url ?? undefined);
          setHeadImageMobile(detail.head_image_mobile_url ?? undefined);
          setStatus('ready');
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, phone]);

  const unclaimed = useMemo(() => coupons.filter((c) => c.claimed !== true), [coupons]);
  const canClaim = unclaimed.length > 0 && claimStatus === 'idle';
  const hasHeadImage = status === 'ready' && (!!headImagePc || !!headImageMobile);
  const showListFooter = status === 'ready' && coupons.length > 0 && claimStatus !== 'done';
  const showResultFooter = status === 'ready' && claimStatus === 'done' && result !== null;

  const doClaim = useCallback(async () => {
    if (claimStatus === 'claiming' || unclaimed.length === 0) return;
    setClaimStatus('claiming');
    const res = await runClaimAll(unclaimed, (c) => claimCoupon(c.code));
    setResult(res);
    setClaimStatus('done');
  }, [claimStatus, unclaimed]);

  // 一键领取入口:未登录先登录,登录成功后续接 doClaim(openLogin 的 onSuccess)
  const handleClaim = useCallback(() => {
    if (!isLoggedIn) {
      openLogin(() => {
        void doClaim();
      });
      return;
    }
    void doClaim();
  }, [isLoggedIn, doClaim]);

  const handleClose = useCallback(() => {
    setMounted(false);
    setTimeout(onClose, CLOSE_ANIM_MS);
  }, [onClose]);

  // 「去使用」:套餐列表已在弹框背后展示,仅关闭弹框留在当前页(不跳 /packages 新页)
  const handleUse = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const handleRetry = useCallback(() => {
    setResult(null);
    setClaimStatus('idle');
  }, []);

  if (!portalMount || !mounted) return null;

  const content = (
    <div
      className="hh-modal__backdrop"
      data-state={mounted ? 'open' : 'closed'}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      role="presentation"
    >
      <div className="hh-modal__card hh-modal__card--coupon">
        <button
          type="button"
          className="hh-coupon-modal__close"
          onClick={handleClose}
          aria-label="关闭"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M18 6L6 18M6 6L18 18" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="hh-coupon-modal__title">领取优惠券</div>

        {/* Banner 位:固定在列表上方,不随滚动(接口返回头图 head_image_pc_url/head_image_mobile_url 时渲染) */}
        {hasHeadImage && (
          <div className="hh-coupon-modal__banner">
            {headImagePc && (
              <img
                className="hh-coupon-modal__banner-img hh-coupon-modal__banner-img--pc"
                src={headImagePc}
                alt=""
              />
            )}
            {headImageMobile && (
              <img
                className="hh-coupon-modal__banner-img hh-coupon-modal__banner-img--mobile"
                src={headImageMobile}
                alt=""
              />
            )}
          </div>
        )}

        {/* 券列表区:红包多时仅此区域滚动 */}
        <div className="hh-coupon-modal__scroll">
          {status === 'loading' && <div className="hh-coupon-modal__loading">加载中...</div>}

          {status === 'error' && <div className="hh-coupon-modal__empty">活动已结束或不可用</div>}

          {status === 'ready' && coupons.length === 0 && claimStatus !== 'done' && (
            <div className="hh-coupon-modal__empty">暂无可领取的优惠券</div>
          )}

          {status === 'ready' && coupons.length > 0 && claimStatus !== 'done' && (
            <div className="hh-coupon-list">
              {coupons.map((c) => (
                <CouponCard key={c.id} coupon={c} />
              ))}
            </div>
          )}

          {status === 'ready' && claimStatus === 'done' && result && (
            <div className="hh-coupon-result">
              <p className="hh-coupon-result__title">
                {result.success > 0 ? '领取成功' : '领取失败'}
              </p>
              <p className="hh-coupon-result__desc">
                {result.success > 0
                  ? `${result.success} 张优惠券已放入「我的红包」${result.failed.length > 0 ? `，${result.failed.length} 张失败` : ''}`
                  : '请稍后重试'}
              </p>
              {result.failed.length > 0 && (
                <ul className="hh-coupon-result__failed">
                  {result.failed.map((f, i) => (
                    <li key={i}>
                      {f.name}：{f.reason}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* 底部按钮区:固定在卡片底部,不随滚动 */}
        {showListFooter && (
          <div className="hh-coupon-modal__footer">
            <button
              type="button"
              className="hh-coupon-modal__submit"
              disabled={!canClaim}
              onClick={handleClaim}
            >
              {claimStatus === 'claiming' ? '领取中...' : canClaim ? '一键领取' : '已全部领取'}
            </button>
          </div>
        )}

        {showResultFooter && result && (
          <div className="hh-coupon-modal__footer">
            {result.success > 0 ? (
              <button type="button" className="hh-coupon-modal__submit" onClick={handleUse}>
                去使用
              </button>
            ) : (
              <div className="hh-coupon-result__actions">
                <button
                  type="button"
                  className="hh-coupon-modal__submit hh-coupon-modal__submit--outline"
                  onClick={handleClose}
                >
                  关闭
                </button>
                <button type="button" className="hh-coupon-modal__submit" onClick={handleRetry}>
                  再试一次
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, portalMount);
}
